import React, { useState, useEffect } from 'react'
import '../Css/AdminCareersApplication.css'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faEdit,
  faTrash,
  faDownload,
  faFilter,
  faFilePdf,
  faFileWord,
  faTimes,
  faSave,
  faEnvelope,
  faPhone,
  faMapMarkerAlt,
  faBuilding,
  faUserTie,
  faMoneyBillWave,
  faLink,
  faSort,
  faSortUp,
  faSortDown,
  faHashtag,
  faUser,
  faBriefcase,
  faCalendar,
  faSpinner,
  faChevronLeft,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons'
import { API_URL } from '../utils/Function'

export default function AdminCareerApplications() {
  const [applications, setApplications] = useState([])
  const [filteredApplications, setFilteredApplications] = useState([])
  const [loading, setLoading] = useState(true)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedApplication, setSelectedApplication] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [careerFilter, setCareerFilter] = useState('all')
  const [careers, setCareers] = useState([])
  const [sortConfig, setSortConfig] = useState({ key: 'applied_at', direction: 'desc' })
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)
  const [currentUser, setCurrentUser] = useState(null)

  // Get current logged-in user from localStorage or context
  useEffect(() => {
    const userData = localStorage.getItem('user')
    if (userData) {
      setCurrentUser(JSON.parse(userData))
    }
  }, [])

  // Fetch applications and careers data
  useEffect(() => {
    fetchApplications()
    fetchCareers()
  }, [])

  // Filter and sort applications when dependencies change
  useEffect(() => {
    filterAndSortApplications()
  }, [applications, searchTerm, statusFilter, careerFilter, sortConfig])

  const fetchApplications = async () => {
    try {
      setLoading(true)
      const response = await fetch(`${API_URL}/career_application.php/get`)
      const data = await response.json()
      if (data.success) {
        setApplications(data.applications)
      } else {
        toast.error('Failed to fetch applications')
      }
    } catch (error) {
      console.error('Error fetching applications:', error)
      toast.error('Error fetching applications')
    } finally {
      setLoading(false)
    }
  }

  const fetchCareers = async () => {
    try {
      const response = await fetch(`${API_URL}/career.php/get`)
      const data = await response.json()
      if (data.success) {
        setCareers(data.careers)
      }
    } catch (error) {
      console.error('Error fetching careers:', error)
    }
  }

  const filterAndSortApplications = () => {
    let filtered = [...applications]

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(app =>
        app.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.phone.includes(searchTerm) ||
        app.career_title?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(app => app.status === statusFilter)
    }

    // Apply career filter
    if (careerFilter !== 'all') {
      filtered = filtered.filter(app => app.career_id == careerFilter)
    }

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1
        }
        return 0
      })
    }

    setFilteredApplications(filtered)
  }

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc'
    })
  }

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return faSort
    return sortConfig.direction === 'asc' ? faSortUp : faSortDown
  }

  const handleEdit = (application) => {
    setSelectedApplication(application)
    setShowEditModal(true)
  }

  const handleCloseEditModal = () => {
    setShowEditModal(false)
    setSelectedApplication(null)
  }

  const handleUpdateApplication = async (updatedData) => {
    try {
      const dataToSend = {
        ...updatedData,
        reviewed_by: currentUser?.id || null
      }

      const response = await fetch(`${API_URL}/career_application.php/put/${selectedApplication.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataToSend)
      })

      const data = await response.json()
      if (data.success) {
        toast.success('Application updated successfully')
        fetchApplications()
        handleCloseEditModal()
      } else {
        toast.error(data.error || 'Failed to update application')
      }
    } catch (error) {
      console.error('Error updating application:', error)
      toast.error('Error updating application')
    }
  }

  const handleDelete = async (applicationId) => {
    if (window.confirm('Are you sure you want to delete this application? This action cannot be undone.')) {
      try {
        const response = await fetch(`${API_URL}/career_application.php/delete/${applicationId}`, {
          method: 'DELETE'
        })

        const data = await response.json()
        if (data.success) {
          toast.success('Application deleted successfully')
          fetchApplications()
        } else {
          toast.error(data.error || 'Failed to delete application')
        }
      } catch (error) {
        console.error('Error deleting application:', error)
        toast.error('Error deleting application')
      }
    }
  }

 const handleDownloadResume = (application) => {
  if (application.resume_url) { 
    const link = document.createElement('a');

    link.href = `${API_URL}/${application.resume_url}`;
    
    link.download = `resume_${application.full_name.replace(/\s+/g, '_')}`;
    link.target = '_blank';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } else {
    toast.error('Resume not available for download');
  }
};


  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { class: 'status-pending', label: 'Pending' },
      reviewed: { class: 'status-reviewed', label: 'Reviewed' },
      shortlisted: { class: 'status-shortlisted', label: 'Shortlisted' },
      rejected: { class: 'status-rejected', label: 'Rejected' },
      hired: { class: 'status-hired', label: 'Hired' }
    }

    const config = statusConfig[status] || { class: 'status-pending', label: status }
    return <span className={`status-badge ${config.class}`}>{config.label}</span>
  }

  const getFileIcon = (filename) => {
    if (filename?.includes('.pdf')) return faFilePdf
    if (filename?.includes('.doc') || filename?.includes('.docx')) return faFileWord
    return faFileWord
  }

  const getFileTypeColor = (filename) => {
    if (filename?.includes('.pdf')) return '#e74c3c'
    if (filename?.includes('.doc') || filename?.includes('.docx')) return '#2c5aa0'
    return '#7f8c8d'
  }

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const currentApplications = filteredApplications.slice(indexOfFirstItem, indexOfLastItem)
  const totalPages = Math.ceil(filteredApplications.length / itemsPerPage)

  const paginate = (pageNumber) => setCurrentPage(pageNumber)

  if (loading) {
    return (
      <div className="admin-career-applications">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading applications...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="admin-career-applications">
      <ToastContainer position="top-right" autoClose={5000} />

      {/* Header */}
      <div className="applications-header">
        <div className="header-content">
          <h1>Career Applications</h1>
          <p>Manage and review job applications</p>
        </div>
      </div>

      <div className="container mt-4">
        <div className="row mb-4">
          <div className="col-md-6">
            <div className="search-box">
              <input
                type="text"
                className="form-control search-input"
                placeholder="Search by name, email, phone, or job title..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="stats-card">
              <div className="stat-item">
                <FontAwesomeIcon icon={faHashtag} className="stat-icons" />
                <div className="stat-info">
                  <span className="stat-number">{applications.length}</span>
                  <span className="stat-label">Total Applications</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="row mb-4">
          <div className="col-md-6">
            <div className="filter-group">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="form-select filter-select"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                {/* <option value="reviewed">Reviewed</option> */}
                <option value="shortlisted">Shortlisted</option>
                <option value="rejected">Rejected</option>
                {/* <option value="hired">Hired</option> */}
              </select>

              <select
                value={careerFilter}
                onChange={(e) => setCareerFilter(e.target.value)}
                className="form-select filter-select"
              >
                <option value="all">All Jobs</option>
                {careers.map(career => (
                  <option key={career.id} value={career.id}>
                    {career.title}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Applications Table */}
        {currentApplications.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              <FontAwesomeIcon icon={faFilter} />
            </div>
            <p className="empty-state-text">
              {searchTerm ? 'No applications found matching your search.' : 'No applications found.'}
            </p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover applications-table-custom">
              <thead className="table-header-custom">
                <tr>
                  <th onClick={() => handleSort('full_name')} className="sortable">
                    <span>Applicant</span>
                    <FontAwesomeIcon icon={getSortIcon('full_name')} className="sort-icon" />
                  </th>
                  <th onClick={() => handleSort('career_title')} className="sortable">
                    <span>Position</span>
                    <FontAwesomeIcon icon={getSortIcon('career_title')} className="sort-icon" />
                  </th>
                  <th>Contact</th>
                  <th onClick={() => handleSort('experience_years')} className="sortable">
                    <span>Experience</span>
                    {/* <FontAwesomeIcon icon={getSortIcon('experience_years')} className="sort-icon" /> */}
                  </th>
                  <th>Resume</th>
                  <th onClick={() => handleSort('applied_at')} className="sortable">
                    <span>Applied Date</span>
                    <FontAwesomeIcon icon={getSortIcon('applied_at')} className="sort-icon" />
                  </th>
                  <th onClick={() => handleSort('status')} className="sortable">
                    <span>Status</span>
                    <FontAwesomeIcon icon={getSortIcon('status')} className="sort-icon" />
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentApplications.map(application => (
                  <tr key={application.id} className="table-row-custom">
                    <td>
                      <div className="applicant-info-custom">
                        <div className="applicant-name-custom">
                          <FontAwesomeIcon icon={faUser} className="me-2 applicant-icon" />
                          {application.full_name}
                        </div>
                        {application.current_designation && (
                          <div className="applicant-designation-custom">
                            <FontAwesomeIcon icon={faUserTie} />
                            {application.current_designation}
                          </div>
                        )}
                        {application.current_company && (
                          <div className="applicant-company-custom">
                            <FontAwesomeIcon icon={faBuilding} />
                            {application.current_company}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="position-info-custom">
                        <div className="position-title-custom">
                          <FontAwesomeIcon icon={faBriefcase} className="me-2" />
                          {application.career_title}
                        </div>
                        <div className="position-department-custom">{application.department}</div>
                      </div>
                    </td>
                    <td>
                      <div className="contact-info-custom">
                        <div className="contact-email-custom">
                          <FontAwesomeIcon icon={faEnvelope} />
                          {application.email}
                        </div>
                        <div className="contact-phone-custom">
                          <FontAwesomeIcon icon={faPhone} />
                          {application.phone}
                        </div>
                        {application.current_location && (
                          <div className="contact-location-custom">
                            <FontAwesomeIcon icon={faMapMarkerAlt} />
                            {application.current_location}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="experience-info-custom">
                        {application.experience_years > 0 ? (
                          <>
                            <span className="experience-years-custom">{application.experience_years} years</span>
                            {application.expected_salary && (
                              <div className="expected-salary-custom">
                                <FontAwesomeIcon icon={faMoneyBillWave} />
                                {application.expected_salary}
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="experience-fresh-custom">Fresh</span>
                        )}
                      </div>
                    </td>
                    <td>
                      {application.resume && (
                        <div className="resume-info-custom">
                          <button
                            className="resume-download-btn-custom"
                            onClick={() => handleDownloadResume(application)}
                            title="Download Resume"
                          >
                            <FontAwesomeIcon 
                              icon={getFileIcon(application.resume)} 
                              style={{ color: getFileTypeColor(application.resume) }}
                            />
                            <span>Download</span>
                          </button>
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="date-info-custom">
                        <div className="applied-date-custom">
                          <FontAwesomeIcon icon={faCalendar} className="me-2" />
                          {new Date(application.applied_at).toLocaleDateString()}
                        </div>
                        <div className="applied-time-custom">
                          {new Date(application.applied_at).toLocaleTimeString()}
                        </div>
                      </div>
                    </td>
                    <td>
                      {getStatusBadge(application.status)}
                    </td>
                    <td className="action-buttons-custom">
                      <button
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={() => handleEdit(application)}
                        title="Edit Application"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(application.id)}
                        title="Delete Application"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination-custom">
            <button
              className="pagination-btn-custom"
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <FontAwesomeIcon icon={faChevronLeft} /> Previous
            </button>
            
            <div className="pagination-page-info">
              Page {currentPage} of {totalPages}
            </div>
            
            <button
              className="pagination-btn-custom"
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        )}
      </div>

      {/* Edit/View Modal */}
      {showEditModal && selectedApplication && (
        <EditApplicationModal
          application={selectedApplication}
          currentUser={currentUser}
          onClose={handleCloseEditModal}
          onUpdate={handleUpdateApplication}
        />
      )}
    </div>
  )
}

// Edit Application Modal Component
function EditApplicationModal({ application, currentUser, onClose, onUpdate }) {
  const [formData, setFormData] = useState({
    status: application.status,
    notes: application.notes || ''
  })
  const [isSaving, setIsSaving] = useState(false)

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSaving(true)
    try {
      await onUpdate(formData)
    } finally {
      setIsSaving(false)
    }
  }

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="edit-application-modal">
        <div className="modal-header">
          <h2>Application Details</h2>
          <button className="close-btn" onClick={onClose} disabled={isSaving}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="modal-body">
          <form onSubmit={handleSubmit}>
            {/* Application Details */}
            <div className="detail-section">
              <h3>Applicant Information</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Full Name:</label>
                  <span>{application.full_name}</span>
                </div>
                <div className="detail-item">
                  <label>Email:</label>
                  <span>{application.email}</span>
                </div>
                <div className="detail-item">
                  <label>Phone:</label>
                  <span>{application.phone}</span>
                </div>
                <div className="detail-item">
                  <label>Location:</label>
                  <span>{application.current_location || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Experience:</label>
                  <span>{application.experience_years} years</span>
                </div>
                <div className="detail-item">
                  <label>Current Company:</label>
                  <span>{application.current_company || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Designation:</label>
                  <span>{application.current_designation || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Expected Salary:</label>
                  <span>{application.expected_salary || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Notice Period:</label>
                  <span>{application.notice_period || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Job Information */}
            <div className="detail-section">
              <h3>Job Information</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Position:</label>
                  <span>{application.career_title}</span>
                </div>
                <div className="detail-item">
                  <label>Department:</label>
                  <span>{application.department}</span>
                </div>
                <div className="detail-item">
                  <label>Applied On:</label>
                  <span>{new Date(application.applied_at).toLocaleString()}</span>
                </div>
              </div>
              {application.reviewed_by_name && (
                <div className="detail-item">
                  <label>Reviewed By:</label>
                  <span>{application.reviewed_by_name}</span>
                </div>
              )}
            </div>

            {/* Links */}
            {(application.portfolio_url || application.linkedin_url) && (
              <div className="detail-section">
                <h3>Links</h3>
                <div className="detail-grid">
                  {application.portfolio_url && (
                    <div className="detail-item">
                      <label>Portfolio:</label>
                      <a href={application.portfolio_url} target="_blank" rel="noopener noreferrer">
                        <FontAwesomeIcon icon={faLink} />
                        View Portfolio
                      </a>
                    </div>
                  )}
                  {application.linkedin_url && (
                    <div className="detail-item">
                      <label>LinkedIn:</label>
                      <a href={application.linkedin_url} target="_blank" rel="noopener noreferrer">
                        <FontAwesomeIcon icon={faLink} />
                        View LinkedIn
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Cover Letter */}
            {application.cover_letter && (
              <div className="detail-section">
                <h3>Cover Letter</h3>
                <div className="cover-letter">
                  {application.cover_letter}
                </div>
              </div>
            )}

            {/* Resume */}
            <div className="detail-section">
              <h3>Resume</h3>
              {application.resume_url ? (
                <div className="resume-section">
                  <button
                    type="button"
                    className="download-resume-btn"
                    onClick={() => {
                      const link = document.createElement('a')
                      link.href = application.resume_url
                      link.download = `resume_${application.full_name.replace(/\s+/g, '_')}`
                      link.target = '_blank'
                      document.body.appendChild(link)
                      link.click()
                      document.body.removeChild(link)
                    }}
                  >
                    <FontAwesomeIcon icon={faDownload} />
                    Download Resume
                  </button>
                </div>
              ) : (
                <p>No resume available</p>
              )}
            </div>

            {/* Editable Fields */}
            <div className="detail-section">
              <h3>Update Status</h3>
              <div className="form-group">
                <label htmlFor="status">Status:</label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="form-input"
                  disabled={isSaving}
                >
                  <option value="pending">Pending</option>
                  <option value="shortlisted">Shortlisted</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="notes">Notes:</label>
                <textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  className="form-textarea"
                  placeholder="Add notes about this application..."
                  rows="4"
                  disabled={isSaving}
                />
              </div>

              {currentUser && (
                <div className="form-group">
                  <label>Will be reviewed by:</label>
                  <div className="reviewer-info">
                    <strong>{currentUser.name}</strong> ({currentUser.email})
                  </div>
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button 
                type="button" 
                className="cancel-btn" 
                onClick={onClose}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="save-btn"
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="fa-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}